import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useMemo } from "react";
import { useProjects, type Project } from "../hooks/useProjects";
// ProjectContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useProjects as useProjectSelection } from "../../../contexts/ProjectContext";
import { useTranslation } from "react-i18next";

type Props = {
  value: string;
  onChange: (projectId: string) => void;
};

const projectLabel = (project: Project): string =>
  project.name ?? project.displayName ?? project.id;

const ProjectSelect = ({ value, onChange }: Props) => {
  const { t, i18n } = useTranslation();
  const { data: projects = [], isLoading } = useProjects();
  const { selectedProjects } = useProjectSelection();

  // A connector can only be scoped to a project the user is currently working
  // in: offer the app-wide project selection, or every project when that
  // selection is "all". The connection's saved project stays listed either way
  // so editing one scoped to a project outside the selection doesn't blank the
  // field or silently drop its scope on save.
  const options = useMemo<Project[]>(() => {
    const selection: string[] = Array.isArray(selectedProjects)
      ? selectedProjects
      : [];
    if (selection.length === 0 || selection.includes("all")) return projects;
    const allowed = new Set(selection);
    return projects.filter(
      (project) => allowed.has(project.id) || project.id === value,
    );
  }, [projects, selectedProjects, value]);

  return (
    <Select
      dir={i18n.dir()}
      value={value || undefined}
      onValueChange={onChange}
    >
      <SelectTrigger className="h-10 w-full rounded-[8px] border-border bg-card text-foreground">
        <SelectValue
          placeholder={
            isLoading
              ? t("mcpPage.loadingProjects")
              : t("mcpPage.selectProject")
          }
        />
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 && !isLoading ? (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            {t("mcpPage.noProjects")}
          </div>
        ) : (
          options.map((project) => (
            <SelectItem key={project.id} value={project.id}>
              {projectLabel(project)}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

export default ProjectSelect;
